import { useState, useEffect, useCallback } from "react";
import {
  contactoAPI,
  Contacto,
  ContactoStatus,
} from "@/services/contactoService";
import { useAuth } from "./useAuth";
import { Contact, CustomerStatus } from "@/types/crm";

export const useContacts = () => {
  const { company } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [statuses, setStatuses] = useState<CustomerStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapContactoToContact = (contacto: Contacto): Contact => {
    return {
      id: contacto.id,
      firstName: contacto.name.split(" ")[0] || "",
      lastName: contacto.name.split(" ").slice(1).join(" ") || "",
      email: contacto.email || "",
      phone: contacto.phone || "",
      position: contacto.position || "",
      notes: contacto.notes || "",
      companyId: contacto.client_company_id || "",
      statusId: contacto.contact_status_id || "",
      countryCode: "",
      linkedinUrl: "",
      avatar: "",
      opportunities: [],
      activities: [],
      attachments: contacto.attachments || [],
      company: contacto.client_company
        ? {
            id: contacto.client_company.id,
            name: contacto.client_company.name,
          }
        : undefined,
      status: contacto.contact_status?.name || "",
      createdAt: contacto.created_at,
      updatedAt: contacto.updated_at,
      lastActivityAt: contacto.last_activity_at,
    } as unknown as Contact;
  };

  const mapStatusToCustomerStatus = (
    status: ContactoStatus,
  ): CustomerStatus => {
    return {
      id: status.id,
      name: status.name,
      description: "",
      color: "#3b82f6",
      order: 0,
      isActive: status.is_active,
      createdAt: status.created_at,
      updatedAt: status.updated_at,
    };
  };

  const loadContacts = useCallback(async () => {
    if (!company?.slug) return;

    setIsLoading(true);
    setError(null);

    try {
      const response: any = await contactoAPI.getContactos(company.slug);
      console.log("Contactos recibidos:", response);
      const contactsList = Array.isArray(response)
        ? response
        : response.data || [];
      const mappedContacts = contactsList.map(mapContactoToContact);
      setContacts(mappedContacts);
    } catch (err: any) {
      console.error("Error al cargar contactos:", err);
      setError(err.response?.data?.message || "Error al cargar contactos");
    } finally {
      setIsLoading(false);
    }
  }, [company?.slug]);

  const loadStatuses = useCallback(async () => {
    if (!company?.slug) return;

    try {
      const response: any = await contactoAPI.getContactoStatuses(company.slug);
      console.log("Estados recibidos:", response);
      const statusesList = Array.isArray(response)
        ? response
        : response.data || [];
      const mappedStatuses = statusesList.map(mapStatusToCustomerStatus);
      setStatuses(mappedStatuses);
    } catch (err: any) {
      console.error("Error al cargar estados:", err);
    }
  }, [company?.slug]);

  useEffect(() => {
    loadContacts();
    loadStatuses();
  }, [loadContacts, loadStatuses]);

  return {
    contacts,
    statuses,
    isLoading,
    error,
    refreshContacts: loadContacts,
    refreshStatuses: loadStatuses,
  };
};
